import { ArrowBack } from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import {
  Card,
  Box,
  Grid,
  FormControlLabel,
  Stack,
  IconButton,
  Dialog,
  DialogContent,
  ListItemButton,
  Link,
  Tooltip,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BiDollarCircle, BiGroup, BiTime } from 'react-icons/bi';
import { BsEye, BsFillPersonFill } from 'react-icons/bs';
import { FaWindowClose } from 'react-icons/fa';
import { FiMoreVertical } from 'react-icons/fi';
import { HiMail, HiOutlineLocationMarker, HiSpeakerphone } from 'react-icons/hi';
import { IoMdSettings } from 'react-icons/io';
import { LuSend } from 'react-icons/lu';
import { MdBookOnline, MdGroup, MdModeEdit } from 'react-icons/md';
import { RiPulseFill } from 'react-icons/ri';
import { useNavigate, useParams } from 'react-router-dom';
import { IJob, amplitude, triggerAiAutomatedCampaign } from 'wisestep-api';
import {
  WsElementWithPopOver,
  WsButton,
  WsColors,
  WsIconText,
  WsTypoColors,
  WsTypoVariants,
  timeSince,
  WsTypography,
  WsDotIcon,
  WsDotColors,
  WsDotTextColors,
  WsIconPublish,
  WsShareJob,
  ShareJobVariant,
  IUserDTO,
  WsIconClosedJob,
  ScrollableButtonTabs,
} from 'wisestep-base';

import IconTextLink from '../../../../components/IconTextLink';
import { useUserContext, useWorkspaceContext } from '../../../../context';
import { ClintPageBreadCrumb } from '../../../../features/clientDetails/helper/clientDetailsHelper';
import { formatJobSalary, reOpenJob } from '../../../../features/jobs/helper';
import CloseJobDialogue from '../../../../features/jobs/listing/components/closeJobDialogue';
import {
  copyStringToClipboard,
  sharingOfJobs,
} from '../../../../features/jobs/listing/components/jobCardRightChild/JobCardRightChild';
import ViewJDLayout from '../../../../features/jobs/listing/components/viewJd';
import JobPageParams from '../../../../features/jobs/types/JobPageParams';
import { checkPermissionForJobAdminTeamOrCreatedUser } from '../../../../features/jobs/utils/JobListSideNavHelper';
import ErrorLogger from '../../../../providers/ErrorLogger';
import { careerPageSettingStore } from '../../../../utils/careerPageSettingsStore';
import getEncodedQueryParams from '../../../../utils/getEncodedQueryParams';
import { success } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import socialMediaShareUrl, { indexJob } from '../../../../utils/shareUrl';
import { getRecruiterAnalyticsUrl } from '../../../candidates/utils/CandidateHelper';
import AcceptJob from '../../../jobs/listing/components/acceptJob';
import {
  amplitudeEventJobCrmPublishJob,
  amplitudeEventJobCrmViewAssignees,
  amplitudeEventJobCrmViewBroadcastJobToVendors,
  amplitudeEventJobCrmViewDetailsJD,
  amplitudeEventJobCrmViewJobPosting,
  amplitudeEventShare,
  amplitudeEventSocialSearch,
} from '../../utils/AmplitudeEvents';
import {
  defaultAISmartSourcePayload,
  JobsCrm,
  jobsCrmRouteNames,
  JobsCrmRouteOptions,
  jobsCrmTabNames,
  locationDisplay,
} from '../helper/JobsCrmHelper';

interface HeaderBannerProps {
  job: IJob;
  jobsCrmType: (type: string) => void;
}

const HeaderBanner = ({ job, jobsCrmType }: HeaderBannerProps) => {
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const params = useParams<JobPageParams>();
  const careerPageSetting = careerPageSettingStore((state) => state.careerPageSettings);
  const workspace = useWorkspaceContext();
  const isAssigned =
    loggedInUser?.email && job.assignedTo?.filter((user) => user.recruiterId == loggedInUser.email);

  const indexJobToCareerPage = async () => indexJob(job, careerPageSetting);
  const [open, setOpen] = useState<boolean>(params.openViewDetails !== 'false');
  const [openCloseDialog, setOpenCloseDialog] = useState<boolean>(false);
  const [openTooltip, setOpenTooltip] = useState<boolean>(false);

  const [type, setType] = useState(params.type ?? 'pipeline');
  const [currentDetailsTab, setCurrentDetailsTab] = useState<number>(
    parseInt(params.defaultJDTab ?? '0')
  );
  const [editJobAdminTeamAllowed, setEditJobAdminTeamAllowed] = useState<boolean>(false);

  const [openJobAdminTeamEdit, setOpenJobAdminTeamEdit] = useState<boolean>(false);
  const [openWorkAssignmentEdit, setOpenWorkAssignmentEdit] = useState<boolean>(false);
  const navigateToPublish = () => navigate(`/jobs/${job.id}/publish`);
  const openViewJD = (
    currentTab: number,
    openAdminTeamEdit: boolean,
    openWorkAssignmentTeamEdit: boolean
  ) => {
    setCurrentDetailsTab(currentTab);
    setOpenJobAdminTeamEdit(openAdminTeamEdit);
    setOpenWorkAssignmentEdit(openWorkAssignmentTeamEdit);
    setOpen(true);
  };
  const setNotification = useNotificationStore((state) => state.setNotification);
  const selectedIndex = (index: number) => {
    setType(jobsCrmRouteNames[index]);
    jobsCrmType(jobsCrmRouteNames[index]);
    const capitalizedRoute =
      jobsCrmRouteNames[index].charAt(0).toUpperCase() + jobsCrmRouteNames[index].slice(1);
    amplitude(`JobCRM_${capitalizedRoute}`, {
      userId: loggedInUser.email,
      clickedOn: capitalizedRoute,
      pageName: 'JobCrm',
      pageUrl: window.location.href,
    });
  };

  const handleCloseClick = async () => {
    if (job?.isClosedForAssigningRecruiters) {
      setNotification(await reOpenJob(job?.id ?? ''));
    } else setOpenCloseDialog(true);
  };

  const handleTriggerAiEmailCampaign = async () => {
    if (job.id) {
      try {
        await triggerAiAutomatedCampaign(job.id, defaultAISmartSourcePayload(job));
        setNotification({
          message: 'Triggered AI email campaign successfully',
          type: 'success',
        });
      } catch (e: any) {
        console.error(e);
        if (e?.response?.status === 400) {
          setNotification({
            message: `${e?.response?.data}`,
            type: 'error',
          });
        } else {
          setNotification({
            message: 'Something went wrong!',
            type: 'error',
          });
        }
      }
    }
  };

  const closeViewJD = () => setOpen(false);
  const navigate = useNavigate();

  const getLocationForSearch = (location) =>
    location.city
      ? location.city
      : location.state
      ? location.state
      : location.country
      ? location.country
      : location.continent;

  const handleSharingOfJobsAcrossPlatforms = (sharingOfJobsPlatform: string) => {
    switch (sharingOfJobsPlatform) {
      case 'Google (Direct)':
        window.open(
          'https://www.google.com/search?q=filetype:(pdf OR doc OR docx OR rtf)  ("' +
            getLocationForSearch(job.locations && job.locations[0]) +
            '") (resume OR cv) -job -sample  ' +
            job.boolQueryFocused
        );
        if (type === JobsCrm.PIPELINE)
          amplitudeEventSocialSearch[JobsCrm.PIPELINE]['google_direct'](loggedInUser);
        else if (type === JobsCrm.SMART_SOURCE)
          amplitudeEventSocialSearch[JobsCrm.SMART_SOURCE]['google_direct'](loggedInUser);
        break;
      case 'Google (LinkedIn)':
        window.open(
          'https://www.google.com/search?q=site:linkedin.com/in ("' +
            getLocationForSearch(job.locations && job.locations[0]) +
            '") ' +
            job?.boolQueryFocused?.replaceAll(/[()]/gm, '')
        );
        if (type === JobsCrm.PIPELINE)
          amplitudeEventSocialSearch[JobsCrm.PIPELINE]['google_linkedin'](loggedInUser);
        else if (type === JobsCrm.SMART_SOURCE)
          amplitudeEventSocialSearch[JobsCrm.SMART_SOURCE]['google_linkedin'](loggedInUser);
        break;
      case 'LinkedIn':
        window.open(
          'https://www.linkedin.com/search/results/people/?keywords=' + job.boolQueryFocused
        );
        if (type === JobsCrm.PIPELINE)
          amplitudeEventSocialSearch[JobsCrm.PIPELINE]['linkedin'](loggedInUser);
        else if (type === JobsCrm.SMART_SOURCE)
          amplitudeEventSocialSearch[JobsCrm.SMART_SOURCE]['linkedin'](loggedInUser);
        break;
      case 'InDeed':
        window.open(
          'https://resumes.indeed.com/search?l=' +
            getLocationForSearch(job.locations && job.locations[0]) +
            '&q=' +
            job.boolQueryFocused
        );
        if (type === JobsCrm.PIPELINE)
          amplitudeEventSocialSearch[JobsCrm.PIPELINE]['indeed'](loggedInUser);
        else if (type === JobsCrm.SMART_SOURCE)
          amplitudeEventSocialSearch[JobsCrm.SMART_SOURCE]['indeed'](loggedInUser);
        break;
      case 'Twitter':
        window.open(
          'https://twitter.com/search?f=user&q=near:"' +
            getLocationForSearch(job.locations && job.locations[0]) +
            '" within:100mi ' +
            job.title
        );
        if (type === JobsCrm.PIPELINE)
          amplitudeEventSocialSearch[JobsCrm.PIPELINE]['twitter'](loggedInUser);
        else if (type === JobsCrm.SMART_SOURCE)
          amplitudeEventSocialSearch[JobsCrm.SMART_SOURCE]['twitter'](loggedInUser);
        break;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setEditJobAdminTeamAllowed(
        await checkPermissionForJobAdminTeamOrCreatedUser(job, loggedInUser)
      );
    };
    fetchData();
  }, [job, loggedInUser]);

  const handleClipBoard = (data: string) => {
    copyStringToClipboard(data);
    setNotification(success('Copied Successfully !!'));
  };

  return (
    <>
      <Card sx={{ bgcolor: WsColors.TheBlues900, borderRadius: 4 }}>
        <Box p={2}>
          <Grid container item xs={12}>
            <Grid item xs={6}>
              <Stack direction="row">
                <FormControlLabel
                  control={
                    <IconButton sx={{ color: WsColors.White }} onClick={() => navigate('/jobs')}>
                      <ArrowBack />
                    </IconButton>
                  }
                  label={
                    <Grid container item xs={12} columnGap={1}>
                      <WsTypography
                        variant={WsTypoVariants.TITLE}
                        color={WsTypoColors.WHITE}
                        cursor="pointer"
                      >
                        {job?.title ?? ''}
                      </WsTypography>
                      <WsDotIcon
                        color={WsDotColors.WHITE}
                        textColor={WsDotTextColors.WHITE}
                        value={job?.jobCode ?? ''}
                      />
                    </Grid>
                  }
                />
                {job?.companyName && !/.*xxxxxx.*/.test(job.companyName) ? (
                  <Box sx={{ pt: 1, ml: -1 }}>
                    <Link
                      href={`/client-details?tabIndex=0&isChild=true&name=${
                        job?.companyName
                      }&clientId=${job?.clientId}&breadCrumbList=${getEncodedQueryParams(
                        ClintPageBreadCrumb['CLIENTS']
                      )}`}
                      underline="none"
                    >
                      <WsTypography
                        variant={WsTypoVariants.TITLE}
                        color={WsTypoColors.PRIMARY}
                        cursor="pointer"
                      >
                        {`@${job?.companyName}`}
                      </WsTypography>
                    </Link>
                  </Box>
                ) : (
                  <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.PRIMARY}>
                    {`@${job?.companyName}`}
                  </WsTypography>
                )}
              </Stack>
            </Grid>
            <Grid item xs={6} container justifyContent="flex-end" columnGap={1}>
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                <Box pt={0.2}>
                  <WsElementWithPopOver
                    element={
                      <WsButton
                        className="social-search"
                        variant="outlined"
                        size="medium"
                        startIcon={<PeopleAltOutlinedIcon />}
                      >
                        Social Search
                      </WsButton>
                    }
                  >
                    <Stack p={1}>
                      {sharingOfJobs?.map((sharingPlatform) => (
                        <ListItemButton
                          key={sharingPlatform.text}
                          onClick={() => handleSharingOfJobsAcrossPlatforms(sharingPlatform.text)}
                        >
                          <WsIconText
                            text={sharingPlatform.text}
                            icon={sharingPlatform.icon}
                            textSize={WsTypoVariants.BODY1}
                            textColor={WsTypoColors.PRIMARY}
                            textCursor="pointer"
                          />
                        </ListItemButton>
                      ))}
                    </Stack>
                  </WsElementWithPopOver>
                </Box>
              </ErrorBoundary>
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                <Box pt={0.2}>
                  <WsShareJob
                    indexJob={indexJobToCareerPage}
                    shareUrl={socialMediaShareUrl(job, careerPageSetting)}
                    shareUrlLinkedIn={socialMediaShareUrl(job, careerPageSetting)}
                    title={job.title ?? ''}
                    isGrey={true}
                    iconSize="small"
                    variant={ShareJobVariant.BUTTON}
                    amplitudeEventHandler={(eventName: string) => {
                      type === JobsCrm.PIPELINE
                        ? amplitudeEventShare[JobsCrm.PIPELINE][eventName](loggedInUser)
                        : amplitudeEventShare[JobsCrm.SMART_SOURCE][eventName](loggedInUser);
                    }}
                    clipBoard={handleClipBoard}
                  />
                </Box>
              </ErrorBoundary>
              <Box pt={0.2}>
                <WsButton
                  variant="outlined"
                  size="medium"
                  startIcon={<WsIconPublish />}
                  onClick={() => {
                    navigateToPublish();
                    amplitudeEventJobCrmPublishJob[type](loggedInUser);
                  }}
                >
                  Publish
                </WsButton>
              </Box>
              <Box pt={0.2}>
                <WsButton
                  variant="outlined"
                  size="medium"
                  startIcon={<BsEye />}
                  onClick={() => {
                    openViewJD(0, false, false);
                    amplitudeEventJobCrmViewDetailsJD[type](loggedInUser);
                  }}
                >
                  View Details
                </WsButton>
              </Box>
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                <Box p={1}>
                  <WsElementWithPopOver element={<FiMoreVertical color={WsColors.White} />}>
                    <Stack p={1} columnGap={1}>
                      {(job.canManageJob ||
                        job.createdBy === loggedInUser?.email ||
                        isEmpty(isAssigned)) && (
                        <>
                          <ListItemButton>
                            <IconTextLink
                              text="Edit job"
                              link={`/jobs/${job.id}/edit`}
                              icon={<MdModeEdit color={WsColors.TheBlues500} />}
                              isPrimary={true}
                            />
                          </ListItemButton>
                          <ListItemButton onClick={handleCloseClick}>
                            <WsIconText
                              text={
                                job?.isClosedForAssigningRecruiters ? 'Reopen Job' : 'Close Job'
                              }
                              icon={
                                job?.isClosedForAssigningRecruiters ? (
                                  <WsIconClosedJob color={WsColors.TheBlues500} />
                                ) : (
                                  <FaWindowClose color={WsColors.TheBlues500} />
                                )
                              }
                              textSize={WsTypoVariants.BODY1}
                              textCursor="pointer"
                              textColor={WsTypoColors.PRIMARY}
                            />
                          </ListItemButton>
                          <ListItemButton>
                            <IconTextLink
                              text="Clone job"
                              link={`/jobs/${job.id}/clone`}
                              icon={
                                <ContentCopyIcon
                                  sx={{ color: WsColors.TheBlues500 }}
                                  fontSize="small"
                                />
                              }
                              isPrimary={true}
                            />
                          </ListItemButton>
                          <ListItemButton onClick={() => openViewJD(2, true, false)}>
                            {editJobAdminTeamAllowed && (
                              <WsIconText
                                text="Change Job Admin Team"
                                icon={<MdGroup color={WsColors.TheBlues500} />}
                                textColor={WsTypoColors.PRIMARY}
                                textSize={WsTypoVariants.BODY1}
                                textCursor="pointer"
                              />
                            )}
                          </ListItemButton>
                          <ListItemButton onClick={() => openViewJD(2, false, true)}>
                            <WsIconText
                              text="Change Work Assignment Team"
                              icon={<BsFillPersonFill color={WsColors.TheBlues500} />}
                              textColor={WsTypoColors.PRIMARY}
                              textSize={WsTypoVariants.BODY1}
                              textCursor="pointer"
                            />
                          </ListItemButton>
                          <ListItemButton>
                            <IconTextLink
                              text="Change Job Attributes"
                              icon={<IoMdSettings color={WsColors.TheBlues500} />}
                              link="/job-fields-settings"
                              isPrimary={true}
                            />
                          </ListItemButton>
                          <ListItemButton>
                            <IconTextLink
                              text="Assign recruiters"
                              icon={<BsFillPersonFill color={WsColors.TheBlues500} />}
                              isPrimary={true}
                              link={`/jobs/${job.id}/assign`}
                            />
                          </ListItemButton>
                        </>
                      )}
                      <ListItemButton
                        onClick={() => {
                          openViewJD(2, false, false);
                          amplitudeEventJobCrmViewAssignees[type](loggedInUser);
                        }}
                      >
                        <WsIconText
                          text="View Assignees"
                          icon={<BsFillPersonFill color={WsColors.TheBlues500} />}
                          textColor={WsTypoColors.PRIMARY}
                          textSize={WsTypoVariants.BODY1}
                          textCursor="pointer"
                        />
                      </ListItemButton>
                      <ListItemButton
                        onClick={() =>
                          amplitudeEventJobCrmViewBroadcastJobToVendors[type](loggedInUser)
                        }
                      >
                        <IconTextLink
                          text="Broadcast Jobs To Vendors "
                          icon={<HiMail color={WsColors.TheBlues500} />}
                          isPrimary={true}
                        />
                      </ListItemButton>
                      <ListItemButton
                        onClick={() => {
                          openViewJD(3, false, false);
                          amplitudeEventJobCrmViewJobPosting[type](loggedInUser);
                        }}
                      >
                        <WsIconText
                          text="View Job Postings"
                          icon={<HiSpeakerphone color={WsColors.TheBlues500} />}
                          onClick={() => {
                            openViewJD(3, false, false);
                            amplitudeEventJobCrmViewJobPosting[type](loggedInUser);
                          }}
                          textColor={WsTypoColors.PRIMARY}
                          textSize={WsTypoVariants.BODY1}
                          textCursor="pointer"
                        />
                      </ListItemButton>
                      {workspace.owns && (
                        <ListItemButton>
                          <IconTextLink
                            text="Trigger AI Email Campaign"
                            icon={<LuSend color={WsColors.TheBlues500} />}
                            onClick={handleTriggerAiEmailCampaign}
                            isPrimary
                            disabled={true}
                          />
                        </ListItemButton>
                      )}
                    </Stack>
                  </WsElementWithPopOver>
                </Box>
              </ErrorBoundary>
            </Grid>
          </Grid>
          <Grid item xs={12} container columnGap={2} ml={3.5}>
            <Stack direction="row">
              <Box pr={1} sx={{ cursor: 'pointer' }}>
                <RiPulseFill color={WsColors.White} />
              </Box>
              <WsTypography
                variant={WsTypoVariants.BODY2}
                color={WsTypoColors.WHITE}
                cursor="pointer"
              >
                {`Posted  ${timeSince(String(job?.createdOn))} by`}
              </WsTypography>
              <Box sx={{ mr: 0.5 }}></Box>
              <WsTypography
                variant={WsTypoVariants.BODY2}
                color={WsTypoColors.PRIMARY}
                cursor="pointer"
                onClick={async () => {
                  const url = job?.createdBy && (await getRecruiterAnalyticsUrl(job?.createdBy));
                  url && navigate(url);
                }}
              >
                {job?.createdBy ?? ''}
              </WsTypography>
            </Stack>
            <Stack direction="row">
              <Box pr={1} sx={{ cursor: 'pointer' }}>
                <RiPulseFill color={WsColors.White} />
              </Box>
              <WsTypography
                variant={WsTypoVariants.BODY2}
                color={WsTypoColors.WHITE}
                cursor="pointer"
              >
                {`Last Updated  ${timeSince(String(job?.createdOn))} by`}
              </WsTypography>
              <Box sx={{ mr: 0.5 }}></Box>
              <WsTypography
                variant={WsTypoVariants.BODY2}
                color={WsTypoColors.PRIMARY}
                cursor="pointer"
                onClick={async () => {
                  const url = job?.createdBy && (await getRecruiterAnalyticsUrl(job?.createdBy));
                  url && navigate(url);
                }}
              >
                {job?.createdBy ?? ''}
              </WsTypography>
            </Stack>
          </Grid>
          <Grid item container xs={12} justifyContent="inherit" mt={2} ml={5}>
            <Grid item container xs={4} alignItems="center" columnSpacing={1}>
              <WsIconText
                icon={<HiOutlineLocationMarker color={WsColors.White} />}
                text={locationDisplay(job)}
                textColor={WsTypoColors.WHITE}
                textSize={WsTypoVariants.SUB_TITLE}
                popOverText="Location"
                textCursor="pointer"
              />
              {job.locations?.length && job.locations.length > 1 && (
                <Tooltip
                  title={job.locations
                    .slice(1)
                    .map((loc) => `${loc.city},${loc.state},${loc.country}`)
                    .join('\n')}
                  open={openTooltip}
                  onClose={() => setOpenTooltip(false)}
                >
                  <Box>
                    <WsTypography
                      onClick={() => setOpenTooltip(true)}
                      cursor="pointer"
                      color={WsTypoColors.PRIMARY}
                    >{`+${job.locations?.length - 1} more`}</WsTypography>
                  </Box>
                </Tooltip>
              )}
            </Grid>
            <Grid item xs={4}>
              <WsIconText
                icon={<BiDollarCircle color={WsColors.White} />}
                text={formatJobSalary(job)}
                textColor={WsTypoColors.WHITE}
                textSize={WsTypoVariants.SUB_TITLE}
                popOverText="Salary"
                textCursor="pointer"
              />
            </Grid>

            <Grid item xs={4}>
              <WsIconText
                icon={<BiGroup color={WsColors.White} />}
                text={
                  job.assignedTo
                    ? `${
                        job.assignedTo.filter((assignedTo) => assignedTo.isAccepted == true).length
                      } of ${job.assignedTo?.length} recruiters accepted`
                    : 'Not Available'
                }
                textColor={WsTypoColors.WHITE}
                textSize={WsTypoVariants.SUB_TITLE}
                popOverText="Number of Recruiters accepted the job"
                textCursor="pointer"
              />
            </Grid>
          </Grid>
          <Grid item container xs={12} mt={2} ml={5}>
            <Grid item xs={4}>
              <WsIconText
                icon={<BiTime color={WsColors.White} />}
                text={job?.isClosedForAssigningRecruiters ? 'Closed' : 'Open'}
                textColor={WsTypoColors.WHITE}
                textSize={WsTypoVariants.SUB_TITLE}
                popOverText="Job Status"
                textCursor="pointer"
              />
            </Grid>
            <Grid item xs={4}>
              <WsIconText
                icon={<MdBookOnline color={WsColors.White} />}
                text={job?.remoteJob ? 'Remote' : job?.workplacePreference ?? 'Not Available'}
                textColor={WsTypoColors.WHITE}
                textSize={WsTypoVariants.SUB_TITLE}
                popOverText="Workplace Preference"
                textCursor="pointer"
              />
            </Grid>
            <Grid item xs={4}>
              <WsIconText
                icon={<HiSpeakerphone color={WsColors.White} />}
                text={
                  job.postedOnJobBoards
                    ? `Published on ${job.postedOnJobBoards.length} of 22 boards`
                    : 'Not Available'
                }
                textColor={WsTypoColors.WHITE}
                textSize={WsTypoVariants.SUB_TITLE}
                popOverText="Number of Job Boards published on"
                textCursor="pointer"
              />
            </Grid>
          </Grid>
        </Box>
        {openCloseDialog && (
          <CloseJobDialogue
            onClose={() => setOpenCloseDialog(false)}
            open={openCloseDialog}
            job={job}
          />
        )}
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          <Dialog
            onClose={closeViewJD}
            aria-labelledby="View Details click"
            open={open}
            maxWidth="lg"
            fullWidth
          >
            <DialogContent style={{ height: '90vh' }}>
              <ViewJDLayout
                job={job}
                closeDialog={closeViewJD}
                defaultTab={currentDetailsTab}
                openJobAdminTeam={openJobAdminTeamEdit}
                openWorkAssignmentTeam={openWorkAssignmentEdit}
              />
            </DialogContent>
          </Dialog>
        </ErrorBoundary>
      </Card>
      <Grid item xs={12} mt={2}>
        <AcceptJob job={job} />
      </Grid>
      <Grid
        item
        xs={12}
        height="35%"
        borderRadius={2}
        sx={{
          background: WsColors.White,
          mt: 2,
        }}
        id="candiateViewScrollable"
        mb={0.5}
      >
        <ScrollableButtonTabs
          scrollList={jobsCrmTabNames}
          getTabIndex={selectedIndex}
          defaultValue={jobsCrmRouteNames.indexOf(type as JobsCrmRouteOptions)}
        />
      </Grid>
    </>
  );
};
export default HeaderBanner;
